import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { api_url } from "../Hooks/Global";

const FranchiseDirector = () => {
    const { franchise_id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [franchise_director_id, setFranchiseDirectorId] = useState(0);
    const [rows, setRows] = useState([]);

    const apiURL = api_url;

    const designationOptions = [
        { value: 'Proprietor', label: 'Proprietor' },
        { value: 'Partner', label: 'Partner' },
        { value: 'Director', label: 'Director' }
    ];

    const [formData, setFormData] = useState({
        director_name: '',
        director_designation: null,
        director_contact_number: '',
        director_email: '',
        director_dob: '',
        director_photo: null,
        director_sign: null,
        is_communication_person: 0
    });

    const [errors, setErrors] = useState({});
    const [photoPreview, setPhotoPreview] = useState('');
    const [signPreview, setSignPreview] = useState('');

    const handleFileChange = (e, field) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file size (max 1MB)
            if (file.size > 1 * 1024 * 1024) {
                Swal.fire({
                    title: "Error",
                    text: "File size should not exceed 1 MB",
                    icon: "error"
                });
                return;
            }

            setFormData(prev => ({ ...prev, [field]: file }));

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                if (field === 'director_photo') {
                    setPhotoPreview(reader.result);
                } else if (field === 'director_sign') {
                    setSignPreview(reader.result);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.director_name?.trim()) newErrors.director_name = "Director name is required";
        if (!formData.director_designation) newErrors.director_designation = "Designation is required";
        if (!formData.director_contact_number?.trim()) {
            newErrors.director_contact_number = "Contact number is required";
        } else if (!/^\d{10}$/.test(formData.director_contact_number)) {
            newErrors.director_contact_number = "Contact number must be 10 digits";
        }
        if (!formData.director_email?.trim()) {
            newErrors.director_email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.director_email)) {
            newErrors.director_email = "Invalid email format";
        }
        if (!formData.director_dob) newErrors.director_dob = "Date of birth is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleDesignationChange = (selectedOption) => {
        setFormData(prev => ({ ...prev, director_designation: selectedOption }));
        setErrors(prev => ({ ...prev, director_designation: null }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setLoading(true);
            const payload = new FormData();

            payload.append("franchise_id", franchise_id);
            payload.append("director_name", formData.director_name);
            payload.append("director_designation", formData.director_designation?.value || '');
            payload.append("director_contact_number", formData.director_contact_number);
            payload.append("director_email", formData.director_email);
            payload.append("director_dob", formData.director_dob);
            payload.append("is_communication_person", formData.is_communication_person);

            if (formData.director_photo && formData.director_photo instanceof File) {
                payload.append("director_photo", formData.director_photo);
            }
            if (formData.director_sign && formData.director_sign instanceof File) {
                payload.append("director_sign", formData.director_sign);
            }
            if (franchise_director_id) {
                payload.append("franchise_director_id", franchise_director_id);
            }

            const response = await axios.post(`${apiURL}Franchise/CreateFranchiseDirector`, payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response?.data?.success) {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: response?.data?.message
                });
                resetForm();
                GetDirectors();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: response?.data?.message || "Failed to save director"
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "An unexpected error occurred"
            });
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFranchiseDirectorId(0);
        setFormData({
            director_name: '',
            director_designation: null,
            director_contact_number: '',
            director_email: '',
            director_dob: '',
            director_photo: null,
            director_sign: null,
            is_communication_person: 0
        });
        setPhotoPreview('');
        setSignPreview('');
        setErrors({});
    };

    const GetDirectors = async () => {
        try {
            const response = await axios.get(`${apiURL}Franchise/GetFranchiseDirector`, {
                params: { franchise_id }
            });

            if (response?.data?.success) {
                setRows(response?.data?.data || []);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const GetInfo = async (franchise_director_id) => {
        try {
            const response = await axios.get(`${apiURL}Franchise/GetFranchiseDirectorInfo`, {
                params: { franchise_director_id }
            });

            if (response?.data?.success) {
                window.scrollTo(0, 0);
                const data = response?.data?.data[0];
                setFranchiseDirectorId(data?.franchise_director_id);

                const designationOption = designationOptions.find(
                    opt => opt.value === data?.director_designation
                );

                setFormData({
                    director_name: data?.director_name || '',
                    director_designation: designationOption || null,
                    director_contact_number: data?.director_contact_number || '',
                    director_email: data?.director_email || '',
                    director_dob: data?.director_dob || '',
                    director_photo: null,
                    director_sign: null,
                    is_communication_person: data?.is_communication_person || 0
                });

                setPhotoPreview(data?.director_photo_url || '');
                setSignPreview(data?.director_sign_url || '');
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleDelete = async (franchise_director_id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.post(`${apiURL}Franchise/DeleteFranchiseDirector`, {
                    franchise_director_id
                });

                Swal.fire('Deleted!', response?.data?.message, 'success');
                GetDirectors();
                if (franchise_director_id === franchise_director_id) {
                    resetForm();
                }
            } catch (error) {
                console.log(error);
                Swal.fire('Error!', 'Something went wrong.', 'error');
            }
        }
    };

    const CompleteSubmision = () => {
        Swal.fire({
            title: "Submission Complete!",
            text: `Your application number is: ${franchise_id}.`,
            icon: "success",
            confirmButtonText: "OK"
        }).then((result) => {
            if (result.isConfirmed) {
                navigate("/");
            }
        });
    };

    useEffect(() => {
        if (franchise_id) {
            GetDirectors();
        }
    }, [franchise_id]);

    return (
        <div id='wrapper'>
            <div className="inner-page-banner-area" style={{ backgroundImage: 'url("/images/About_images/about_banner.jpg")' }}>
                <div className="container">
                    <div className="pagination-area">
                        <h1>Franchise Director Management</h1>
                    </div>
                </div>
            </div>

            <div className="registration-page-area">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card shadow-sm">
                                <div className="card-header bg-primary">
                                    <h4 className="mb-0 text-white">
                                        {franchise_director_id ? "Update Director" : "Add New Director"}
                                    </h4>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row">
                                            <div className="col-lg-6 col-md-6 col-sm-12">
                                                <div className="form-group mb-3">
                                                    <label className="control-label">Director Name <span className="text-danger">*</span></label>
                                                    <input
                                                        type="text"
                                                        name="director_name"
                                                        value={formData.director_name}
                                                        onChange={handleInputChange}
                                                        className="form-control"
                                                        placeholder="Enter director's full name"
                                                    />
                                                    {errors.director_name && <small className="text-danger">{errors.director_name}</small>}
                                                </div>
                                            </div>

                                            <div className="col-lg-6 col-md-6 col-sm-12">
                                                <div className="form-group mb-3">
                                                    <label className="control-label">Designation <span className="text-danger">*</span></label>
                                                    <Select
                                                        name="director_designation"
                                                        value={formData.director_designation}
                                                        onChange={handleDesignationChange}
                                                        options={designationOptions}
                                                        placeholder="Select designation"
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                    />
                                                    {errors.director_designation && <small className="text-danger">{errors.director_designation}</small>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-lg-6 col-md-6 col-sm-12">
                                                <div className="form-group mb-3">
                                                    <label className="control-label">Email <span className="text-danger">*</span></label>
                                                    <input
                                                        type="email"
                                                        name="director_email"
                                                        value={formData.director_email}
                                                        onChange={handleInputChange}
                                                        className="form-control"
                                                        placeholder="Enter email address"
                                                    />
                                                    {errors.director_email && <small className="text-danger">{errors.director_email}</small>}
                                                </div>
                                            </div>

                                            <div className="col-lg-6 col-md-6 col-sm-12">
                                                <div className="form-group mb-3">
                                                    <label className="control-label">Contact Number <span className="text-danger">*</span></label>
                                                    <input
                                                        type="tel"
                                                        name="director_contact_number"
                                                        value={formData.director_contact_number}
                                                        onChange={handleInputChange}
                                                        className="form-control"
                                                        placeholder="Enter contact number"
                                                        maxLength="10"
                                                    />
                                                    {errors.director_contact_number && <small className="text-danger">{errors.director_contact_number}</small>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-lg-6 col-md-6 col-sm-12">
                                                <div className="form-group mb-3">
                                                    <label className="control-label">Date of Birth <span className="text-danger">*</span></label>
                                                    <input
                                                        type="date"
                                                        name="director_dob"
                                                        value={formData.director_dob}
                                                        onChange={handleInputChange}
                                                        className="form-control"
                                                    />
                                                    {errors.director_dob && <small className="text-danger">{errors.director_dob}</small>}
                                                </div>
                                            </div>

                                            <div className="col-lg-6 col-md-6 col-sm-12">
                                                <div className="form-group mb-3">
                                                    <div className="form-check mt-4">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id="is_communication_person"
                                                            name="is_communication_person"
                                                            checked={formData.is_communication_person === 1}
                                                            onChange={handleInputChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="is_communication_person">
                                                            Communication Person
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-lg-6 col-md-6 col-sm-12">
                                                <div className="form-group mb-3">
                                                    <label className="control-label">Photo</label>
                                                    <input
                                                        type="file"
                                                        name="director_photo"
                                                        onChange={(e) => handleFileChange(e, 'director_photo')}
                                                        accept="image/*"
                                                        className="form-control"
                                                    />
                                                    {photoPreview && (
                                                        <div className="mt-2">
                                                            <img
                                                                src={photoPreview}
                                                                alt="Photo Preview"
                                                                style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '10px' }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-lg-6 col-md-6 col-sm-12">
                                                <div className="form-group mb-3">
                                                    <label className="control-label">Signature</label>
                                                    <input
                                                        type="file"
                                                        name="director_sign"
                                                        onChange={(e) => handleFileChange(e, 'director_sign')}
                                                        accept="image/*"
                                                        className="form-control"
                                                    />
                                                    {signPreview && (
                                                        <div className="mt-2">
                                                            <img
                                                                src={signPreview}
                                                                alt="Signature Preview"
                                                                style={{ width: '150px', height: '60px', objectFit: 'contain' }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-lg-12">
                                                <div className="d-flex gap-2 justify-content-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={resetForm}
                                                    >
                                                        Reset
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={loading}
                                                    >
                                                        {loading ? "Processing..." : (franchise_director_id ? "Update" : "Submit")}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                    {rows?.length > 0 && (
                        <div className="row mt-5">
                            <div className="col-lg-12">
                                <div className="card shadow-sm">
                                    <div className="card-header bg-primary text-white">
                                        <h4 className="mb-0">Director List</h4>
                                    </div>
                                    <div className="card-body">
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-hover">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Photo</th>
                                                        <th>Name</th>
                                                        <th>Signature</th>
                                                        <th>Designation</th>
                                                        <th>Email</th>
                                                        <th>Contact</th>
                                                        <th>Communication</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows?.map((row, index) => (
                                                        <tr key={index}>
                                                            <td className="text-center">
                                                                {row?.director_photo_url ? (
                                                                    <img
                                                                        src={row?.director_photo_url}
                                                                        alt={row?.director_name}
                                                                        className="rounded-circle"
                                                                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <i className="fa fa-user fa-2x text-muted"></i>
                                                                )}
                                                            </td>
                                                            <td>{row?.director_name}</td>
                                                            <td className="text-center">
                                                                {row?.director_sign_url ? (
                                                                    <img
                                                                        src={row?.director_sign_url}
                                                                        alt="signature"
                                                                        style={{ width: '80px', height: '30px', objectFit: 'contain' }}
                                                                    />
                                                                ) : (
                                                                    <span className="text-muted">No signature</span>
                                                                )}
                                                            </td>
                                                            <td>{row?.director_designation}</td>
                                                            <td>{row?.director_email}</td>
                                                            <td>{row?.director_contact_number}</td>
                                                            <td className="text-center">
                                                                {row?.is_communication_person === 1 ? (
                                                                    <span className="badge bg-success">Yes</span>
                                                                ) : (
                                                                    <span className="badge bg-secondary">No</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => GetInfo(row?.franchise_director_id)}
                                                                    >
                                                                        <i className="fa fa-edit"></i>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDelete(row.franchise_director_id)}
                                                                        className="btn btn-sm btn-outline-danger"
                                                                    >
                                                                        <i className="fa fa-trash"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="row mt-4">
                        <div className="col-lg-12">
                            <div className="d-flex justify-content-end gap-2">
                                <button className='btn btn-primary' onClick={CompleteSubmision}>
                                    Complete Submission
                                </button>
                                <Link to="/" className="btn btn-secondary">
                                    Back to Home
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FranchiseDirector;